import { useState } from 'react'

import { Box, Button, HeaderText, Text } from '@factorialco/deprecated-design-system'
import { F0Box, F0Button, F0Heading, F0Text } from '@factorialco/f0-react'
import { DocumentsDataTableDocument } from 'generated/resources/datatable.graphql'
import { UpdateStatusFilledDocument } from 'generated/resources/graphql'
import { DocumentForPreviewDocument } from 'generated/resources/graphql'
import { DocumentsDataCollectionDocument } from 'generated/resources/graphql'
import { DocumentStatusesEnum } from 'generated/resources/graphql'

import { useAsyncData } from 'modules/core/hooks/useAsyncData'
import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useMutation } from 'modules/core/hooks/useMutation'
import asyncAction from 'modules/core/lib/asyncAction'
import i18n from 'modules/core/lib/i18n'
import app from 'modules/core/store/models/app'

import { Document } from 'modules/documents/hooks/documents/useGetDocuments'
import { canFill, isPendingToFill } from 'modules/documents/lib/Documents'
import Logs from 'modules/documents/store/collections/logs'
import type { EsignatureRequest } from 'modules/esignatures/hooks/requests/useGetEsignatureRequests'

import FillableModal from './Modal'
import SignatureRequests from './SignatureRequests'
import FillableStatus from './Status'

interface Props {
  document: Document
  signatureRequests: EsignatureRequest[]
  saving: boolean
  disableFilleableConfirm: boolean
  useF0?: boolean
}

const FillablePDF = ({
  document,
  signatureRequests,
  saving,
  disableFilleableConfirm,
  useF0 = false,
}: Props) => {
  const { employee: currentEmployee } = useCurrentSession()
  const [openModal, setOpenModal] = useState(false)
  const [disabledButton, setDisabledButton] = useState(false)
  const [logs] = useState(new Logs())
  const descriptionText = (status: DocumentStatusesEnum) => description[status]
  const [updateStatusFilled] = useMutation(UpdateStatusFilledDocument, {
    extractorFn: (data) => data.documentsMutations.updateStatusFilledDocumentsDocument,
  })
  const description: Record<DocumentStatusesEnum, string> = {
    to_fill: (currentEmployee ? canFill(document, currentEmployee.id) : false)
      ? i18n.t('documents.modal.fillable.description.to_fill')
      : i18n.t('documents.modal.fillable.description.to_fill_others'),
    filled: i18n.t('documents.modal.fillable.description.filled'),
  }

  const { loading } = useAsyncData(
    async () =>
      Promise.all([logs.fetch({ data: { document_id: document.id, log_type: 'status_changed' } })]),
    [document.id]
  )

  const additionalSignees = signatureRequests.filter(
    (
      request
    ): request is EsignatureRequest & { signee: NonNullable<EsignatureRequest['signee']> } => {
      if (request.signee == null) return false
      const employee = document.employee
      return request.signee.id !== (employee && employee.access.id)
    }
  )

  const {
    access: { id: accessId },
  } = useCurrentSession()
  const accessSignatureRequest = signatureRequests.filter(
    (request) => request.signee?.id == accessId
  )

  const onSubmit = async () => {
    setDisabledButton(true)
    const { success } = await asyncAction(() =>
      updateStatusFilled({
        variables: {
          id: document.id,
        },
        refetchQueries: [
          DocumentsDataTableDocument,
          DocumentForPreviewDocument,
          DocumentsDataCollectionDocument,
        ],
      })
    )

    if (success) {
      // This event allow the mobile webview to know that the document was filled
      window.postMessage({ event: 'factorialFilled', documentId: document.id })

      setOpenModal(false)
      setDisabledButton(false)
    } else {
      app.setFlash({
        type: 'wrong',
        message: i18n.t('errors.generic_saving'),
      })
    }
  }

  if (useF0) {
    return (
      <F0Box gap="lg">
        {openModal && (
          <FillableModal
            useF0
            saving={saving}
            disabledButton={disabledButton}
            onSubmit={onSubmit}
            onClose={() => setOpenModal(false)}
            signatureRequests={accessSignatureRequest}
          />
        )}
        {document.status && (
          <F0Box gap="xs" paddingBottom="md">
            <F0Heading variant="heading" content={i18n.t('documents.modal.fillable.title')} />
            <F0Text variant="description" content={descriptionText(document.status)} />
          </F0Box>
        )}
        {document.status && (
          <FillableStatus
            useF0
            statusChangedLog={logs.models.at(-1)}
            documentStatus={document.status}
            employeeFullName={document.employee?.fullName ?? ''}
            employeeAvatarURL={document.employee?.avatar?.url ?? ''}
          />
        )}
        <F0Box gap="md">
          {isPendingToFill(document) && (
            <SignatureRequests
              useF0
              document={document}
              loading={loading}
              additionalSignees={additionalSignees}
            />
          )}
          {currentEmployee && canFill(document, currentEmployee.id) && (
            <F0Box
              display="flex"
              flexDirection="row"
              justifyContent="end"
              width="full"
              paddingTop="md"
            >
              <F0Button
                disabled={disableFilleableConfirm}
                onClick={() => setOpenModal(true)}
                label={i18n.t('documents.modal.fillable.modal.confirm')}
              />
            </F0Box>
          )}
        </F0Box>
      </F0Box>
    )
  }

  return (
    <Box gap="s24">
      {openModal && (
        <FillableModal
          saving={saving}
          disabledButton={disabledButton}
          onSubmit={onSubmit}
          onClose={() => setOpenModal(false)}
          signatureRequests={accessSignatureRequest}
        />
      )}
      {document.status && (
        <Box gap="s8">
          <HeaderText text={i18n.t('documents.modal.fillable.title')} />
          <Text size="300" color="grey800">
            {descriptionText(document.status)}
          </Text>
        </Box>
      )}
      {document.status && (
        <FillableStatus
          statusChangedLog={logs.models.at(-1)}
          documentStatus={document.status}
          employeeFullName={document.employee?.fullName ?? ''}
          employeeAvatarURL={document.employee?.avatar?.url ?? ''}
        />
      )}
      <Box gap="s16">
        {isPendingToFill(document) && (
          <SignatureRequests
            document={document}
            loading={loading}
            additionalSignees={additionalSignees}
          />
        )}
        {currentEmployee && canFill(document, currentEmployee.id) && (
          <Box alignItems="flexEnd">
            <Button disabled={disableFilleableConfirm} onClick={() => setOpenModal(true)}>
              {i18n.t('documents.modal.fillable.modal.confirm')}
            </Button>
          </Box>
        )}
      </Box>
    </Box>
  )
}

export default FillablePDF
